import Vue from 'vue'
import to from 'await-to-js'
import 'array-flat-polyfill'
import Axios from '@/config/axios'
import { EventBus, EVENTS } from '@/services/event-bus'
import client from '@/api/client'
import UserService from '@/api/services/_shared/user-service'
import populateOrder from '@/api/gql/checkout/mutation/populate-order.gql'
import addGiftCardToCart from '@/api/gql/checkout/mutation/add-gift-card-to-cart.gql'
import { isEmpty } from '@/helpers/object'
import { isNumber } from '@/helpers/number'
import {
  fetchCurrentOrder,
  applyPromotionToCurrentOrder,
  removePromotionFromCurrentOrder,
  completeCurrentOrder,
  resetOrderPayment,
} from '@/components/Navigation/MiniCart/order-queries'

export const ORDER_STATES = {
  cart: 'cart',
  address: 'address',
  delivery: 'delivery',
  payment: 'payment',
  confirm: 'confirm',
  complete: 'complete',
  exported: 'exported',
}

export const COMPLETED_ORDER_STATES = [ORDER_STATES.complete, ORDER_STATES.exported]

export const TAX_TYPES = {
  shipment: 'shipment',
}

export function itemTotal(items) {
  return items?.length > 0 ? items.map(i => i.price * i.quantity).reduce((sum, i) => sum + i) : 0
}

export function shippingTotal(
  items,
  shippingThreshold,
  shippingCost,
  shippingCostAboveThreshold,
  shippingCostBelowThreshold,
  selectedShippingRate
) {
  if (isEmpty(selectedShippingRate)) {
    if (shippingThreshold && isNumber(shippingThreshold)) {
      return itemTotal(items) >= shippingThreshold
        ? shippingCostAboveThreshold
        : shippingCostBelowThreshold
    } else {
      return shippingCost
    }
  } else {
    return parseFloat(selectedShippingRate.price || 0)
  }
}

export function promotionTotal(promotionAdjustment) {
  return parseFloat((promotionAdjustment && promotionAdjustment.amount) || 0)
}

export function getGiftCardsTotal(payments) {
  if (!payments) return 0
  const giftCardsTotal = payments
    .filter(({ paymentMethodType }) => paymentMethodType === 'givex')
    .reduce((sum, item) => sum + item.amount, 0)
  return parseFloat(-giftCardsTotal || 0)
}

export function promotionCode(promotionAdjustment) {
  return promotionAdjustment?.code || ''
}

export function taxLines(taxAdjustments) {
  return (taxAdjustments || []).map(({ label, amount }) => ({
    label,
    amount: parseFloat(amount),
  }))
}

export function taxTotal(taxAdjustments) {
  return (taxAdjustments || []).reduce((sum, { amount }) => sum + parseFloat(amount), 0)
}

export function totalWithoutStoreCreditsUsed(
  itemTotal,
  shippingTotal,
  promotionTotal,
  giftCardsTotal,
  taxTotal,
  isTaxIncluded,
  canShowTaxLines
) {
  return (
    itemTotal +
    shippingTotal +
    promotionTotal +
    giftCardsTotal +
    (isTaxIncluded || !canShowTaxLines ? 0 : taxTotal)
  )
}

export function total(totalWithoutStoreCreditsUsed, storeCreditsUsed, isExcludedFromStoreCredit) {
  return !isExcludedFromStoreCredit
    ? totalWithoutStoreCreditsUsed - storeCreditsUsed
    : totalWithoutStoreCreditsUsed
}

function getOrderAndLineItemErrorMessage(order, variantId) {
  const orderValidationErrors = order?.errors || {}
  const lineItemValidationErrors =
    order?.items?.find(item => item.variantId == variantId)?.errors || {}
  const validationErrors = { ...orderValidationErrors, ...lineItemValidationErrors }
  if (!isEmpty(validationErrors)) {
    const errorMessage = Object.values(validationErrors).flat().join(', ')
    return errorMessage
  }
  return null
}

const defaultState = {
  promotionAdjustment: {},
  taxAdjustments: [],
  isTaxIncluded: true,
  canShowTaxLines: true,
  dataIsInitialized: false,
  completedAt: null,
  containsSocks: false,
  cartItemIsLoading: false,
  adjustmentIsLoading: false,
  adyenTotalAmount: 0,
  payments: [],
  quickAdding: false,
  items: [],
  number: '',
  guestToken: '',
  mergedItemIndex: null,
  promoCode: '',
  promoDiscount: 0,
  paths: {
    termsAndConditionsUrl: '',
    privacyPolicyUrl: '',
  },
  shippingRates: [],
  isRegistrationValid: false, // TODO B2C-1129 remove in favour of state
  isShippingValid: false, // TODO B2C-1129 remove in favour of state
  isPaymentValid: false, // TODO B2C-1129 remove in favour of state
  isCyclon: false,
  state: ORDER_STATES.cart,
  email: '',
  shippingAddress: {},
  billingAddress: {},
  preorderDeliveryDate: null,
  checkoutVersion: 1,
  errors: {},
}

export default {
  namespaced: true,
  state: { ...defaultState },
  mutations: {
    setState(state, payload) {
      for (let key in payload) {
        state[key] = payload[key]
      }
    },
    setPaths(state, paths) {
      for (const key in paths) {
        state.paths[key] = paths[key]
      }
    },
    unsetIsRegistrationValid(state) {
      state.isRegistrationValid = false
    },
    unsetIsShippingValid(state) {
      state.isShippingValid = false
    },
    setDataInitialized(state, payload = true) {
      state.dataIsInitialized = payload
    },
    revertItemsOrder(state) {
      state.items.reverse()
    },
    setQuickAdding(state, payload) {
      state.quickAdding = payload
    },
    cartItemIsLoading(state, payload) {
      state.cartItemIsLoading = payload
    },
    adjustmentIsLoading(state, payload) {
      state.adjustmentIsLoading = payload
    },
    updateCartItemNumber(state, payload) {
      state.items[payload.index].quantity = payload.quantity
    },
    setAdjustment(state, payload) {
      state.promotionAdjustment = payload
    },
    setShippingRates(state, payload) {
      state.shippingRates = payload
    },
    resetShippingRates(state) {
      state.shippingRates = []
    },
    setShippingAddress(state, payload) {
      state.shippingAddress = payload
    },
    removeBillingAddress(state) {
      state.billingAddress = {}
    },
    updateSelectedShippingRate(state, selectedShippingRateId) {
      state.shippingRates.forEach(({ id }, key) => {
        Vue.set(state.shippingRates[key], 'selected', id === selectedShippingRateId)
      })
      state.taxAdjustments = state.taxAdjustments.filter(obj => obj.type !== TAX_TYPES.shipment)
    },
  },
  actions: {
    setOrderState({ commit }, order) {
      commit('setState', order)
    },
    async fetchData({ dispatch }, { noCache = false } = {}) {
      const { currentOrder, paths } = await fetchCurrentOrder(noCache)
      dispatch('handleFetchData', { currentOrder, paths })
    },

    async completeOrder({ commit }, variables) {
      const result = await completeCurrentOrder(variables)
      const { order } = result.data.completeCurrentOrder
      if (order.errors) throw order.errors
      commit('setState', order)
      return result
    },

    resetState({ commit }) {
      commit('setState', { ...defaultState })
    },

    handleFetchData({ commit, rootState }, { currentOrder, paths }) {
      commit('setState', currentOrder)
      if (paths) commit('setPaths', paths)
      commit('setDataInitialized')
      if (currentOrder?.items) commit('revertItemsOrder')

      if (currentOrder || rootState.recommendations) {
        const skus = currentOrder?.items?.map(item => item.sku) || []
        Vue.$dyClient.updateRecommendation(skus)
      }
      window.dispatchEvent(new CustomEvent('on:order-ready'))
    },

    updateCartItemNumber({ state, commit, dispatch }, payload) {
      commit('updateCartItemNumber', {
        index: getItemIndex(state, payload),
        quantity: payload.quantity,
      })
      if (!isEmpty(state.promotionAdjustment)) commit('adjustmentIsLoading', true)
      dispatch('updateServer', {
        skuId: payload.skuId,
        quantity: payload.increase,
        onDone: payload.onDone,
        onError: payload.onError,
      }).then(() => {
        commit('adjustmentIsLoading', false)
        EventBus.$emit(EVENTS.minicart.update, {
          detail: { addToCart: true },
        })
      })
    },

    async updateServer(
      { state, dispatch },
      { skuId, quantity, isGiftCardOrder, giftCardMessage, onDone, onError }
    ) {
      const isNewItem = getItemIndex(state, { skuId }) === -1
      if (isNewItem) dispatch('startCartNewItemPreperation')
      const [error, response] = await to(
        isGiftCardOrder
          ? updateServerMutation(addGiftCardToCart, {
              variantId: skuId,
              quantity: 1,
              giftCardMessage,
            })
          : updateServerMutation(populateOrder, { variantId: skuId, quantity })
      )

      if (error) {
        dispatch('fetchData', { noCache: true })
        if (isNewItem) dispatch('finishCartNewItemPreperation')
        if (error) Vue.rollbar.error(error)
        onError && onError()
      } else {
        const currentOrder = isGiftCardOrder
          ? response.data.addGiftCardToCart.order
          : response.data.populateOrder.order
        const validationErrorMessages = getOrderAndLineItemErrorMessage(currentOrder, skuId)
        if (isNewItem) dispatch('finishCartNewItemPreperation')
        dispatch('handleFetchData', { currentOrder })
        if (validationErrorMessages) onError && onError(validationErrorMessages)
        else onDone && onDone(currentOrder)
      }
    },

    startCartNewItemPreperation({ commit }) {
      commit('cartItemIsLoading', true)
      // Don't show empty cart if product is being added but takes a while to show up
      commit('setQuickAdding', true)
    },

    finishCartNewItemPreperation({ commit }) {
      commit('cartItemIsLoading', false)
      commit('setQuickAdding', false)
    },

    redirectToCheckout({ rootState }, { onError }) {
      return Axios.patch(`/${rootState.page.locale}/cart?checkout`, {})
        .then(function (response) {
          if (response.data.success) window.location.href = `/${rootState.page.locale}/checkout`
        })
        .catch(function (error) {
          const message = error.response.data.errors.join(', ')
          onError && onError(message)
        })
    },

    async applyPromoCode({ commit }, promoCode) {
      const [error, result] = await to(applyPromotionToCurrentOrder(promoCode))
      if (error) throw { type: 'error', text: '' }
      const data = result.applyPromotionToCurrentOrder
      if (data.messageType == 'success') {
        commit('setState', data.order)
        return { type: data.messageType, text: data.messageText }
      } else {
        throw { type: data.messageType, text: data.messageText }
      }
    },

    async clearPromoCode({ commit }, promoCode) {
      const [error, result] = await to(removePromotionFromCurrentOrder(promoCode))
      if (error) throw { type: 'error', text: '' }
      const data = result.removePromotionFromCurrentOrder
      commit('setState', data)
    },

    async removeAssociatedUser({ dispatch }) {
      const response = await to(UserService.logout({ keepCurrentOrder: true }))
      await dispatch('fetchData', { noCache: true })
      return response
    },

    async resetOrderPayment({ commit }) {
      const data = await to(resetOrderPayment())
      commit('setState', data)
    },
  },

  getters: {
    adjustmentIsLoading: state => state.adjustmentIsLoading,
    quickAdding: state => state.quickAdding,
    dataIsInitialized: state => state.dataIsInitialized,
    items: state => state.items,
    itemsLength: state => state.items.length,
    isEmpty: state => state.items.length <= 0,
    itemsCount: state => state.items.reduce((sum, item) => sum + item.quantity, 0),
    itemTotal: state => (state.dataIsInitialized ? itemTotal(state.items) : 0),
    usedStoreCredit: (state, getters, rootState, rootGetters) =>
      Math.min(
        getters.totalWithoutStoreCreditsUsed,
        rootGetters['account/availableStoreCreditTotal']
      ),
    remainingStoreCredit: (state, getters, rootState, rootGetters) =>
      rootGetters['account/availableStoreCreditTotal'] - getters.usedStoreCredit,
    isStoreCreditUsed: (state, getters) =>
      !state.isExcludedFromStoreCredit && getters.usedStoreCredit > 0,
    isExcludedFromStoreCredit: state => state.isExcludedFromStoreCredit,
    shippingTotal: (state, getters, rootState, rootGetters) =>
      shippingTotal(
        state.items,
        rootGetters['store/shippingThreshold'],
        rootGetters['store/shippingCost'],
        rootGetters['store/shippingCostAboveThreshold'],
        rootGetters['store/shippingCostBelowThreshold'],
        state.shippingRates.find(obj => obj.selected)
      ),
    promotionTotal: state => promotionTotal(state.promotionAdjustment),
    promotionCode: state => promotionCode(state.promotionAdjustment),
    giftCardsTotal: state => getGiftCardsTotal(state.payments),
    taxLines: state => taxLines(state.taxAdjustments),
    taxTotal: state => taxTotal(state.taxAdjustments),
    isTaxIncluded: state => state.isTaxIncluded,
    canShowTaxLines: state => state.canShowTaxLines,
    totalWithoutStoreCreditsUsed: (state, getters) =>
      totalWithoutStoreCreditsUsed(
        getters.itemTotal,
        getters.shippingTotal,
        getters.promotionTotal,
        getters.giftCardsTotal,
        getters.taxTotal,
        getters.isTaxIncluded,
        getters.canShowTaxLines
      ),
    total: (state, getters) =>
      total(
        getters.totalWithoutStoreCreditsUsed,
        getters.usedStoreCredit,
        state.isExcludedFromStoreCredit
      ),
    promoCode: state => state.promotionAdjustment && state.promotionAdjustment.code,
    promoDiscount: state => (state.promotionAdjustment && state.promotionAdjustment.amount) || 0,
    promotionAdjustment: state => state.promotionAdjustment,
    isGiftCardOrder: state => state.items.some(i => i.productType === 'gift_cards'),
    mergedItemIndex: state => state.mergedItemIndex,
    selectedShippingRate: state => state.shippingRates?.find(({ selected }) => selected),
    defaultShippingRate: (state, getters) =>
      getters.selectedShippingRate || state.shippingRates?.[0],
    selectedShippingRateId: (state, getters) => getters.selectedShippingRate?.id || -1,
    itemOutOfStock: state => state.items.some(i => i.stock === 0 || i.quantity > i.stock),
    paths: state => state.paths,
    shippingAddress: state => state.shippingAddress,
    canProcessPayment: state =>
      (state.state === ORDER_STATES.payment || state.state === ORDER_STATES.confirm) &&
      state.adyenTotalAmount > 0,
    canCompleteOrder: state =>
      (state.state === ORDER_STATES.payment || state.state === ORDER_STATES.confirm) &&
      state.adyenTotalAmount === 0,
    isPreorder: state => !!state.items?.some(i => i.variant?.isPreorderable),
    isCyclon: state => state.items?.some(i => i.groupName === 'Cyclon'),
  },
}

// Helper functions
function getItemIndex(state, payload) {
  return state.items.findIndex(item => {
    return Number(item.variantId) === payload.skuId
  })
}

function updateServerMutation(mutationName, variables) {
  return client.mutate({
    mutation: mutationName,
    variables: { ...variables },
  })
}
